import * as React from 'react';

import { QueryClientProvider } from '@tanstack/react-query';

import { ModuleList } from 'root/apps/ModuleCompareUI/ModuleCompare.types';
import { queryClientFactory } from 'root/apps/PurchaseOrder/NuorderOrdering/context/QueryClient';

export const LOG_SOURCE = 'ModuleCompare';

export interface ModuleCompareState {
  accountId: string | null;
  secondAccountId: string | null;
  activeMode: 'byId' | 'byFile' | null;
  currentAccountModuleList: ModuleList | null;
  comparedModules: ModuleList | null;
}

export const blankModuleCompareState: ModuleCompareState = {
  accountId: null,
  secondAccountId: null,
  activeMode: null,
  currentAccountModuleList: null,
  comparedModules: null,
};

export const ModuleCompareActionTypes = {
  SET_SECOND_ACCOUNT_ID: 'SET_SECOND_ACCOUNT_ID',
  SET_ACTIVE_MODE: 'SET_ACTIVE_MODE',
  SET_COMPARED_MODULES: 'SET_COMPARED_MODULES',
  SET_CURRENT_ACCOUNT_MODULE_LIST: 'SET_CURRENT_ACCOUNT_MODULE_LIST',
  ADD_MODULE_TO_ACCOUNT: 'ADD_MODULE_TO_ACCOUNT',
  DELETE_MODULE_FROM_ACCOUNT: 'DELETE_MODULE_FROM_ACCOUNT',
  BULK_ADD_MODULE_TO_ACCOUNT: 'BULK_ADD_MODULE_TO_ACCOUNT',
} as const;

export type ModuleCompareActionTypes =
  (typeof ModuleCompareActionTypes)[keyof typeof ModuleCompareActionTypes];

type ModuleCompareActionPayloads = {
  [ModuleCompareActionTypes.SET_SECOND_ACCOUNT_ID]: string | null;
  [ModuleCompareActionTypes.SET_ACTIVE_MODE]: 'byId' | 'byFile' | null;
  [ModuleCompareActionTypes.SET_COMPARED_MODULES]: ModuleList | null;
  [ModuleCompareActionTypes.SET_CURRENT_ACCOUNT_MODULE_LIST]: ModuleList;
  [ModuleCompareActionTypes.ADD_MODULE_TO_ACCOUNT]: {
    moduleId: string;
    accountId: string;
  };
  [ModuleCompareActionTypes.DELETE_MODULE_FROM_ACCOUNT]: {
    moduleId: string;
    accountId: string;
  };
  [ModuleCompareActionTypes.BULK_ADD_MODULE_TO_ACCOUNT]: {
    modules: ModuleList;
    fromAccountId: string;
    toAccountId: string;
  };
};

type ModuleCompareReducerAction = {
  [K in keyof ModuleCompareActionPayloads]: {
    type: K;
    payload: ModuleCompareActionPayloads[K];
  };
}[keyof ModuleCompareActionPayloads];

const ModuleCompareReducer: React.Reducer<ModuleCompareState, ModuleCompareReducerAction> = (
  newState,
  action
): ModuleCompareState => {
  const state: ModuleCompareState = { ...newState };

  switch (action.type) {
    case ModuleCompareActionTypes.SET_SECOND_ACCOUNT_ID:
      state.secondAccountId = action.payload;
      return state;
    case ModuleCompareActionTypes.SET_ACTIVE_MODE:
      if (state.activeMode === action.payload) return state;
      state.activeMode = action.payload;
      return state;
    case ModuleCompareActionTypes.SET_COMPARED_MODULES:
      state.comparedModules = action.payload;
      return state;
    case ModuleCompareActionTypes.SET_CURRENT_ACCOUNT_MODULE_LIST:
      state.currentAccountModuleList = action.payload;
      return state;
    case ModuleCompareActionTypes.ADD_MODULE_TO_ACCOUNT:
      if (!state.comparedModules) return state;
      state.comparedModules[action.payload.moduleId][action.payload.accountId] = true;
      state.comparedModules = { ...state.comparedModules };
      return state;
    case ModuleCompareActionTypes.DELETE_MODULE_FROM_ACCOUNT:
      if (!state.comparedModules) return state;
      state.comparedModules[action.payload.moduleId][action.payload.accountId] = false;
      state.comparedModules = { ...state.comparedModules };
      return state;
    case ModuleCompareActionTypes.BULK_ADD_MODULE_TO_ACCOUNT:
      if (!state.comparedModules) return state;
      const updatedModules = { ...state.comparedModules };
      Object.keys(action.payload.modules).forEach((moduleId) => {
        if (
          updatedModules[moduleId][action.payload.fromAccountId] &&
          !updatedModules[moduleId][action.payload.toAccountId]
        ) {
          updatedModules[moduleId] = {
            ...updatedModules[moduleId],
            [action.payload.toAccountId]: true,
          };
        }
      });
      state.comparedModules = updatedModules;
      return state;
    default:
      return state;
  }
};

export interface ModuleCompareContextProps {
  accountId: string;
  state: ModuleCompareState;
  dispatch: React.Dispatch<ModuleCompareReducerAction>;
}

const ModuleCompareContext: React.Context<ModuleCompareContextProps> = React.createContext(
  {} as ModuleCompareContextProps
);

interface ModuleCompareProviderProps {
  accountId: string;
  children: React.ReactNode;
  mockState?: ModuleCompareState; // Optional for testing purposes
}

export const ModuleCompareProvider: React.FC<ModuleCompareProviderProps> = ({
  accountId,
  children,
  mockState = {},
}) => {
  const [state, dispatch] = React.useReducer(ModuleCompareReducer, {
    ...blankModuleCompareState,
    ...mockState,
  });

  const queryClient = React.useMemo(() => {
    return queryClientFactory();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ModuleCompareContext.Provider value={{ accountId, state, dispatch }}>
        {children}
      </ModuleCompareContext.Provider>
    </QueryClientProvider>
  );
};

export const useModuleCompare = () => {
  const context = React.useContext(ModuleCompareContext);
  if (context.state === undefined) {
    throw new Error('useModuleCompare must be used within a ModuleCompareProvider');
  }
  return context;
};
